import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import {
    ProcessorThreeDSModeField,
    THREE_DS_MODE_DEFAULT,
    type ThreeDSMode,
} from '../../components/processor-threeds-mode-field'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'
import { SecretInput } from '@/components/form/secret-input'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { AdyenEditSkeleton } from '../components/adyen-edit-skeleton'

const AdyenEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const { t } = useTranslation('processors')
    const { toast } = useToast()
    const processorExit = useProcessorEditExit()

    const { data: processor, isLoading } = useProcessorById(id)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        testMode: false,
        dryMode: false,
        apiKey: '',
        merchantAccount: '',
        clientKey: '',
        hmacKey: '',
        liveEndpointPrefix: '',
        descriptor: '',
        threeDSMode: THREE_DS_MODE_DEFAULT as ThreeDSMode,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                testMode: options?.testMode ?? false,
                dryMode: options?.dryMode === true,
                apiKey: options?.apiKey || '',
                merchantAccount: options?.merchantAccount || '',
                clientKey: options?.clientKey || '',
                hmacKey: options?.hmacKey || '',
                liveEndpointPrefix: options?.liveEndpointPrefix || '',
                descriptor: options?.descriptor?.descriptor || '',
                threeDSMode: (options?.threeDSMode as ThreeDSMode) || THREE_DS_MODE_DEFAULT,
            })
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }
        if (!formData.apiKey.trim()) {
            setValidationError(t('adyen.validation.apiKeyRequired'))
            return
        }
        if (!formData.merchantAccount.trim()) {
            setValidationError(t('adyen.validation.merchantAccountRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    name: formData.name,
                    type: 'adyen',
                    enabled: formData.enabled,
                    options: {
                        testMode: formData.testMode,
                        dryMode: formData.dryMode,
                        apiKey: formData.apiKey,
                        merchantAccount: formData.merchantAccount,
                        clientKey: formData.clientKey,
                        hmacKey: formData.hmacKey,
                        liveEndpointPrefix: formData.liveEndpointPrefix,
                        threeDSMode: formData.threeDSMode,
                        descriptor: {
                            descriptor: formData.descriptor,
                        },
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))

    if (isLoading) return <AdyenEditSkeleton />

    if (!processor || processor.type !== 'adyen') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('adyen.editTitle')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('apiCredentials')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('adyen.credentials.howToGetTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('adyen.credentials.step1')}</li>
                                <li>{t('adyen.credentials.step2')}</li>
                                <li>{t('adyen.credentials.step3')}</li>
                                <li>{t('adyen.credentials.step4')}</li>
                            </ol>
                        </div>

                        <div>
                            <SecretInput
                                label={t('adyen.credentials.apiKeyLabel') + ' *'}
                                value={formData.apiKey}
                                onChange={(value) => setFormData({ ...formData, apiKey: value })}
                                placeholder="AQE..."
                            />
                        </div>

                        <div>
                            <Label htmlFor="merchantAccount">{t('adyen.credentials.merchantAccountLabel')} *</Label>
                            <Input
                                id="merchantAccount"
                                value={formData.merchantAccount}
                                onChange={(e) => setFormData({ ...formData, merchantAccount: e.target.value })}
                                placeholder="YourCompanyECOM"
                            />
                        </div>

                        <div>
                            <Label htmlFor="clientKey">{t('adyen.credentials.clientKeyLabel')}</Label>
                            <Input
                                id="clientKey"
                                value={formData.clientKey}
                                onChange={(e) => setFormData({ ...formData, clientKey: e.target.value })}
                                placeholder="test_XXXXXX or live_XXXXXX"
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('adyen.credentials.clientKeyDescription')}
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="liveEndpointPrefix">{t('adyen.credentials.liveEndpointPrefixLabel')}</Label>
                            <Input
                                id="liveEndpointPrefix"
                                value={formData.liveEndpointPrefix}
                                onChange={(e) => setFormData({ ...formData, liveEndpointPrefix: e.target.value })}
                                placeholder="1797a841fbb37ca7-AdyenTechTest"
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('adyen.credentials.liveEndpointPrefixDescription')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('adyen.webhook.cardTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('adyen.webhook.howToConfigureTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('adyen.webhook.step1')}</li>
                                <li>{t('adyen.webhook.step2')}</li>
                                <li>{t('adyen.webhook.step3')}</li>
                                <li>{t('adyen.webhook.step4')}</li>
                            </ol>
                        </div>

                        <div>
                            <SecretInput
                                label={t('adyen.webhook.hmacKeyLabel')}
                                value={formData.hmacKey}
                                onChange={(value) => setFormData({ ...formData, hmacKey: value })}
                                placeholder={t('adyen.webhook.hmacKeyPlaceholder')}
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('testModeSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="testMode"
                                checked={formData.testMode}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, testMode: checked === true })
                                }
                            />
                            <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                        </div>

                        {formData.testMode && (
                            <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                                <p className="text-sm text-yellow-700">
                                    ⚠️ {t('adyen.testMode.warning')}
                                </p>
                                <a
                                    href="https://docs.adyen.com/development-resources/testing/test-card-numbers"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block text-sm text-blue-600 underline hover:text-blue-800"
                                >
                                    {t('viewTestCardNumbers')} →
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('statementDescriptor')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="descriptor">{t('descriptor')}</Label>
                            <Input
                                id="descriptor"
                                value={formData.descriptor}
                                onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
                                maxLength={22}
                                placeholder={t('descriptorPlaceholder')}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('adyen.descriptor.description')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <ProcessorThreeDSModeField
                    value={formData.threeDSMode}
                    onChange={(mode) => setFormData({ ...formData, threeDSMode: mode })}
                    disabled={updateProcessorMutation.isPending}
                />

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const AdyenEditPage = withRequiredPermission(AdyenEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
